<template>
  <section
    :class="{ CarouselProducts_stopped: stopped, CarouselProducts_paused: paused }"
    class="CarouselProducts"
  >
    <div
      ref="progress"
      class="CarouselProducts__progress"
    />
    <div
      v-show="showAlert && alert"
      :data-alert="alert"
      :show-alert="showAlert"
      class="home__alert-container"
    >
      <button
        class="home__alert-button"
        @click="showAlert = false"
      >
        &times;
      </button>
      <div
        class="home__alert-content"
        v-html="alert"
      />
    </div>
    <transition-group
      ref="products"
      :move-class="moveClass"
      tag="ul"
      class="CarouselProducts__products"
      @mouseenter.native="pause"
      @mouseleave.native="play"
      @focusin.native="pause"
      @focusout.native="play"
    >
      <li
        v-for="(product, index) in currentProducts"
        :key="product"
        :aria-hidden="index === 2 ? 'false' : 'true'"
        class="CarouselProducts__product"
      >
        <CarouselProductsProduct
          :id="product"
          :index="products.indexOf(product)"
        />
      </li>
    </transition-group>
    <button
      class="CarouselProducts__button CarouselProducts__button_back"
      @click="back(true)"
    >
      <BaseArrow
        direction="left"
        class="CarouselProducts__arrow"
      />
    </button>
    <button
      class="CarouselProducts__button CarouselProducts__button_forward"
      @click="forward(true)"
    >
      <BaseArrow
        direction="right"
        class="CarouselProducts__arrow"
      />
    </button>
  </section>
</template>

<script>
  import Hammer from 'hammerjs';

  import CarouselProductsProduct from './CarouselProductsProduct';

  export default {
    components: {
      CarouselProductsProduct,
    },
    props: {
      alert: {
        type: String,
        default: null,
      },
      products: {
        type: Array,
        required: true,
      },
    },
    data() {
      const { products } = this;

      return {
        showAlert: true,
        stopped: undefined,
        moveClass: undefined,
        intervalHandle: undefined,
        currentProducts: [...products.slice(-2), ...products.slice(0, -2)],
      };
    },
    computed: {
      currentProductIndex: {
        get() {
          return this.products.indexOf(this.currentProduct);
        },
        set(productIndex) {
          const { products } = this;
          const { length } = products;

          this.currentProduct = products[((productIndex % length) + length) % length];
        },
      },
      paused: {
        get() {
          return !this.intervalHandle;
        },
        set(paused) {
          this[paused ? 'play' : 'pause']();
        },
      },
    },
    mounted() {
      const hammer = new Hammer(this.$refs.products.$el);

      hammer.on('swiperight', () => { this.back(true); });
      hammer.on('swipeleft', () => { this.forward(true); });

      if (window.innerWidth >= 768) {
        this.start();
      }
    },
    methods: {
      play() {
        if (this.stopped) {
          return;
        }

        clearInterval(this.intervalHandle);

        this.intervalHandle = setInterval(this.forward, 7500);
      },
      pause() {
        if (this.stopped) {
          return;
        }

        clearInterval(this.intervalHandle);

        // eslint-disable-next-line no-void
        void this.$refs.progress.offsetWidth;

        this.intervalHandle = null;
      },
      start() {
        this.stopped = false;

        this.play();
      },
      stop() {
        this.pause();

        this.stopped = true;
      },
      back(stop = false) {
        if (stop) {
          this.stop();
        }

        const { currentProducts } = this;

        this.moveClass = 'CarouselProducts__product_move-back';

        currentProducts.unshift(currentProducts.pop());
      },
      forward(stop = false) {
        if (stop) {
          this.stop();
        }

        const { currentProducts } = this;

        this.moveClass = 'CarouselProducts__product_move-forward';

        currentProducts.push(currentProducts.shift());
      },
    },
  };
</script>

<style lang="scss">
  .CarouselProducts {
    position: relative;
    margin-right: auto;
    margin-left: auto;
    max-width: $max-width;

    @include breakpoint(phablet) {
      padding-right: spacing(base-beta) * 2;
      padding-left: spacing(base-beta) * 2;
    }
  }

  .CarouselProducts__arrow {
    fill: color(white);
    transition-property: fill;
    transition-duration: duration(fast);
    transition-timing-function: timing-function(standard);

    .CarouselProducts__button:focus &,
    .CarouselProducts__button:hover & {
      fill: color(slate);
    }
  }

  .CarouselProducts__button {
    $size: 48px;

    position: absolute;
    top: 50%;
    display: none;
    width: $size;
    height: $size;
    padding: spacing(base-alpha);
    transform: translateY(-50%);
    background-color: color(bronze);

    &:focus,
    &:hover {
      outline-style: none;
    }

    @include breakpoint(phablet) {
      display: block;
    }
  }

  .CarouselProducts__button_back {
    left: spacing(base-beta) * 2;
  }

  .CarouselProducts__button_forward {
    right: spacing(base-beta) * 2;
  }

  .CarouselProducts__products {
    display: flex;
    overflow-x: hidden;
  }

  .CarouselProducts__product {
    flex-shrink: 0;
    width: 100%;
    margin-right: calc(200% - #{spacing(base-alpha) * 10});
    margin-left: calc(-200% + #{spacing(base-alpha) * 10});
    padding: spacing(base-alpha);
    width: calc(100% - #{spacing(base-alpha) * 4});

    @include breakpoint(phablet) {
      margin-left: -200%;
      margin-right: 200%;
      width: 100%;
      padding: 0;
    }
  }

  .CarouselProducts__product_move-back,
  .CarouselProducts__product_move-forward {
    transition-property: transform;
    transition-duration: duration(fast);
    transition-timing-function: timing-function(standard);

    @include breakpoint(phablet) {
      transition-duration: duration(slow);
    }
  }

  .CarouselProducts__product_move-back {
    &:first-child {
      transition-duration: 1ms;
    }
  }

  .CarouselProducts__product_move-forward {
    &:last-child {
      transition-duration: 1ms;
    }
  }

  .CarouselProducts__progress {
    display: none;

    @include breakpoint(phablet) {
      display: block;
    }

    &::before {
      @keyframes progress {
        from {
          transform: scaleX(0);
        }

        to {
          transform: scaleX(1);
        }
      }

      display: block;
      height: spacing(base-alpha);
      background-color: color(bronze);
      transform-origin: left;
      content: "";
      position: relative;
      z-index: 12;

      .CarouselProducts:not(.CarouselProducts_paused) & {
        animation-name: progress;
        animation-duration: 7500ms;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
      }
    }
  }

  .home__alert-content a {
    text-decoration: underline;

    &:hover,
    &:focus {
      color: color(slate);
    }
  }

  .home__alert-container {
    position: absolute;
    // left: 0;
    bottom: 100%;
    z-index: 11;
    // width: 100%;
    background-color: rgba(color(red), 0.9);
    color: color(white);

    @include breakpoint(phablet) {
      right: spacing(base-beta) * 2;
      left: spacing(base-beta) * 2;
      bottom: calc(100% - #{spacing(base-alpha)});
    }
  }

  .home__alert-content {
    $padding: 2 * spacing(base-beta);

    @include container;

    padding: $padding $padding * 2 $padding $padding;
    line-height: 1.5;
  }

  .home__alert-button {
    $spacing: spacing(base-beta);

    position: absolute;
    right: $spacing;
    top: $spacing;
    display: flex;
    width: $spacing * 2;
    height: $spacing * 2;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: color(white);
    cursor: pointer;

    &:hover,
    &:focus {
      color: color(slate);
    }
  }
</style>
